import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import { ConversationsState } from '../../../lib/conversations';
import { Me } from '../../../lib/me';
import { NavRail } from '../NavRail';
import { WorkspaceNav } from '../WorkspaceNav';
import { ScrollArea } from '../ui';
import { ConversationsSection } from './components/ConversationsSection';
import { AccountCard } from './components/AccountCard';
import { CommunityLink } from './components/CommunityLink';
import type { NavId } from './hooks/use-sidebar-nav';

/**
 * The WIDE rail — a light floating card that can collapse to an icon strip.
 * The narrow drawer is a separate, dark UI (./mobile/MobileSidebar); both read the
 * same models from ./hooks.
 */
export function WideSidebar({
  state,
  selectedId,
  navCurrent,
  me,
  collapsed = false,
  onExpand,
  onOpenSearch,
}: {
  state: ConversationsState;
  selectedId: string | null;
  navCurrent?: NavId;
  me: Me | null;
  // Rail collapse state (visuals are pure CSS off the rail width; this flag only
  // drives behavior — the collapsed search glyph opens the search flyout instead
  // of focusing the inline field).
  collapsed?: boolean;
  // Expand the collapsed rail. Clicking the collapsed strip anywhere that ISN'T
  // an icon/link expands it (the icons keep their own navigate/toggle action).
  onExpand?: () => void;
  onOpenSearch?: () => void;
}) {
  // The card keeps a small (8px) outer seam off the grey field; the breathing room
  // lives INSIDE the card as content padding. `contentPad` insets the content column
  // from the card edges when expanded, and drops to 0 when collapsed so the icons
  // stay centered on the rail's shared x28 column. It animates on the same curve as
  // the rail width so the collapse stays one smooth motion.
  // In collapsed the content column is 260px but clips at ~57px. A left inset of
  // 11px (≈ Figma's 12/62 ratio scaled to 57px) centers 36px-wide icon items at
  // x≈29 — matching the icon-strip center in the Figma collapsed frame.
  const contentPad = collapsed ? 11 : 12;
  const railCurve = '420ms cubic-bezier(0.32, 0.72, 0, 1)';

  const handleRailClick = (e: React.MouseEvent) => {
    if (!collapsed || !onExpand) return;
    if ((e.target as HTMLElement).closest('a,button,input,select,textarea,[role="button"]')) return;
    onExpand();
  };

  return (
    <aside
      className="orion-sidebar orion-sidebar--wide"
      data-open="true"
      onClick={handleRailClick}
      style={{
        cursor: collapsed ? 'pointer' : undefined,
        position: 'absolute',
        insetInlineStart: 0,
        top: 0,
        bottom: 0,
        zIndex: 6,
        transition:
          'transform 420ms cubic-bezier(0.32, 0.72, 0, 1), width 420ms cubic-bezier(0.32, 0.72, 0, 1), background-color 420ms ease',
        // The rail itself is transparent — the white surface is a FLOATING CARD
        // (the layer below) lifting off the grey field with a gap on every side,
        // the Symphony dashboard look.
        backgroundColor: 'transparent',
        padding: 0,
      }}
    >
      {/* The white card surface. Flush to the rail on every side (no gutter) — it
          fills the aside edge-to-edge and meets the content pane at --sidebar-w. Sits
          BEHIND the content column (which is transparent) so the collapse-morph clip
          is unaffected: the card is a pure background layer that shrinks with the rail
          width. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgb(var(--surface-raised-rgb))',
          borderInlineEnd: '1px solid rgba(var(--ink-rgb), 0.05)',
          boxShadow: '0 1px 3px rgba(var(--ink-rgb), 0.04), 0 12px 32px rgba(var(--ink-rgb), 0.06)',
          pointerEvents: 'none',
        }}
      />
      {/* Collapse is a pure SLIDE: this content is a fixed 260px-wide column, and the
          rail's animating width (--sidebar-w) clips it via overflow-x. So collapsing
          just slides the labels out under the right edge while the icons stay exactly
          where they are — no layout swap, no reflow, nothing pops or jumps. The
          `collapsed` flag only drives the rail width (in AppFrame), not the content. */}
      <div style={{ position: 'relative', zIndex: 1, width: 260, height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', paddingInline: contentPad, transition: `padding ${railCurve}` }}>
        {/* Top block: the primary nav. The brand mark (BrandHeader) floats over the
            top-left at y=24, so this block clears it with 60px (24 logo top + 36 logo height). */}
        <div style={{ padding: '60px 0 0', flexShrink: 0 }}>
          {/* Nav items sit directly under the "sidebar" root so their
              componentParentName is "sidebar" (no intermediate section). */}
          <NavRail current={navCurrent} collapsed={collapsed} onOpenSearch={collapsed ? onOpenSearch : undefined} />
        </div>

        {/* Workspaces, then chat history, fill the middle — as ONE scroll region, not
            two. Workspaces and chats each used to scroll independently, so a long
            workspace list buried the chats with no way to reach them. Now the whole
            middle scrolls together and the sections flow at natural height. */}
        <ScrollArea
          bar="hidden"
          style={{ flex: 1, display: 'flex', flexDirection: 'column', maskImage: 'linear-gradient(to bottom, transparent 0px, black 20px, black calc(100% - 20px), transparent 100%)', WebkitMaskImage: 'linear-gradient(to bottom, transparent 0px, black 20px, black calc(100% - 20px), transparent 100%)' }}
        >
          <WorkspaceNav collapsed={collapsed} />
          <GenericEventsRouteProvider value="conversations">
            <ConversationsSection state={state} selectedId={selectedId} collapsed={collapsed} />
          </GenericEventsRouteProvider>
        </ScrollArea>

        {/* Bottom cluster — the community link, then the account card (the design's rail foot). The
            bell icon is inlined into the account row itself.
            Figma `Sidebar-Menu` 2679:3158 puts "Join Symphony community" at the TOP of this cluster,
            12px above the credits card — below the scroll's fade, so the chat list dissolves into it
            rather than running under a row that can scroll away. */}
        <div style={{ flexShrink: 0, paddingInline: 0, paddingTop: 12, paddingBottom: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <GenericEventsRouteProvider value="community">
            <CommunityLink variant="rail" collapsed={collapsed} />
          </GenericEventsRouteProvider>
          <GenericEventsRouteProvider value="account">
            <AccountCard me={me} collapsed={collapsed} />
          </GenericEventsRouteProvider>
        </div>
      </div>
    </aside>
  );
}
